import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import './about.css';

const About = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const ripples = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;

    const resizeCanvas = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let lastRippleTime = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ripples.current = ripples.current.filter(r => r.alpha > 0);

      ripples.current.forEach(ripple => {
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);

        ctx.strokeStyle = `rgba(255, 255, 255, ${ripple.alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ripple.radius += 0.1;
        ripple.alpha -= 0.0002;
      });

      requestAnimationFrame(draw);
    };

    const addRipple = e => {
      const now = Date.now();
      if (now - lastRippleTime < 400) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      ripples.current.push({ x, y, radius: 0, alpha: 0.3 });
      lastRippleTime = now;
    };

    container.addEventListener('mousemove', addRipple);
    draw();

    return () => {
      container.removeEventListener('mousemove', addRipple);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What is Cavitation Technology?
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Text Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-lg text-gray-600 leading-relaxed">
              Cavitation uses the rapid formation and collapse of microbubbles in a liquid to generate localized energy, high shear, and turbulence. This enhances mixing, oxidation, and reactions — without chemicals or excessive heat.
            </p>

            {/* --- START: ADDED KEY FEATURES --- */}
            <div className="mt-10">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Key Features:
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                {/* Feature 1: Chemical-Free */}
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.414 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">
                    Chemical-Free
                  </span>
                </div>

                {/* Feature 2: High Energy Efficiency */}
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">
                    High Energy Efficiency
                  </span>
                </div>

                {/* Feature 3: Rapid Process Enhancement */}
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">
                    Rapid Process Enhancement
                  </span>
                </div>

                {/* Feature 4: Scalable & Modular */}
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 14a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 14a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">
                    Scalable & Modular
                  </span>
                </div>
              </div>
            </div>
            {/* --- END: ADDED KEY FEATURES --- */}

          </motion.div>

          {/* Video with Ripple */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div
              ref={containerRef}
              className="ripple-container aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-xl relative cursor-pointer"
            >
              <video
                ref={videoRef}
                src="vid2.mp4"
                loop
                playsInline
                controls
                className="object-cover w-full h-full"
                S draggable={false}
              />
              <canvas ref={canvasRef} className="ripple-canvas" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-600 rounded-lg -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;